import { Button, Dropdown, ScrollArea, SidebarProvider } from '@ibsys/kit';
import { ChevronDownIcon } from 'lucide-react';
import { CSSProperties, FC, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router-dom';
import { useConversation } from '@/entities/conversations';
import {
  applyNbspTypography,
  cn,
  SCROLL_AREA_VIEWPORT_BLOCK,
  scrollIntoViewWithinContainer,
  useHashScroll,
  useScrollSpy,
} from '@/shared/lib';
import { useMainLayout } from '@/shared/ui';
import {
  buildGroupedNavigationData,
  type GroupedHierarchyInput,
  MAIN_FIELDS_NODE_ID,
  ROOT_GENERAL_NODE_ID,
} from '../../lib';
import type { GroupWithAttributes } from '../../lib/dialog-data-derived';
import {
  type AttributeDefinitionsMap,
  type NavigationNode,
  useCollapsibleSections,
  useNavigationCollapse,
  useNavigationWidth,
  useTopLevelNodesOrder,
} from '../../model';
import { ContentScroller } from './ContentScroller';
import { NavigationResizeHandle } from './NavigationResizeHandle';
import { NavigationSidebar } from './NavigationSidebar';

/** Префикс DOM `id` секции в «портянке» атрибутов. */
const SECTION_ID_PREFIX = 'attr-section-';

/** Возвращает DOM `id` секции по идентификатору узла навигации. */
const toSectionId = (nodeId: string): string => `${SECTION_ID_PREFIX}${nodeId}`;

/** Извлекает идентификатор узла из DOM `id` секции (или null). */
const fromSectionId = (sectionId: string | null): string | null => {
  if (!sectionId) return null;
  return sectionId.startsWith(SECTION_ID_PREFIX) ? sectionId.slice(SECTION_ID_PREFIX.length) : sectionId;
};

/** Ширина свёрнутой панели навигации по атрибутам — место под один переключатель. */
const COLLAPSED_NAVIGATION_WIDTH = '3rem';

/** Узел навигации в плоском виде — для построения списка id и dropdown'а на мобиле. */
type FlatNode = { id: string; name: string; depth: number };

/** Рекурсивно флатит дерево узлов в плоский список с сохранением глубины. */
const flattenNodes = (nodes: NavigationNode[], depth = 0): FlatNode[] =>
  nodes.flatMap((node) => [{ id: node.id, name: node.name, depth }, ...flattenNodes(node.children ?? [], depth + 1)]);

type Props = {
  conversationId?: number;
  /** Группы атрибутов диалога с заполненными значениями (в порядке отображения). */
  groupsWithValues?: GroupWithAttributes[];
  /** Показывать верхнеуровневый узел «Общее» с `MainFieldsPanel` (метаданные диалога). */
  showMainFields?: boolean;
};

/**
 * Таб «Атрибуты» секции данных диалога.
 *
 * Раскладка:
 * - Слева — навигационное дерево (или dropdown на мобиле).
 * - Справа — единая прокручиваемая «портянка» со всеми секциями.
 *
 * Иерархия верхнего уровня (системный порядок, поверх которого действует
 * пользовательский порядок блоков из настроек):
 * 1. «Общее» — метаданные диалога (если `showMainFields`).
 * 2. «Без группы» — значения атрибутов, не входящих ни в одну видимую группу.
 * 3. Группы атрибутов — в порядке, заданном в `groupsWithValues`.
 *
 * Внутри каждого узла группы — стандартная иерархия из путей атрибутов
 * (вложенные структуры, массивы и т.д.).
 *
 * Активный узел в навигации синхронизируется со скроллом через `useScrollSpy`,
 * а URL hash используется как «глубокая ссылка» на конкретную секцию.
 */
export const DialogDataTab: FC<Props> = ({ conversationId, groupsWithValues, showMainFields }) => {
  const { t } = useTranslation(['dialogs']);
  const { data: conversation } = useConversation(conversationId);
  const { isMobile } = useMainLayout();
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const sidebarContextRef = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();
  const { pathname, search } = useLocation();

  /** Дедуплицированный набор определений всех атрибутов из видимых групп (по `path`). */
  const attributeDefinitions = useMemo<AttributeDefinitionsMap>(() => {
    const map: AttributeDefinitionsMap = new Map();
    for (const { attributes } of groupsWithValues ?? []) {
      for (const attr of attributes) map.set(attr.path, attr);
    }
    return map;
  }, [groupsWithValues]);

  /** Преобразование `groupsWithValues` в формат входа `buildGroupedNavigationData`. */
  const groupsForHierarchy = useMemo<GroupedHierarchyInput[]>(() => {
    return (groupsWithValues ?? []).map(({ group, attributes }) => {
      const topLevelPaths = new Set<string>();
      for (const attr of attributes) topLevelPaths.add(attr.path.split('.')[0]);
      return { code: group.code, name: group.name, topLevelPaths };
    });
  }, [groupsWithValues]);

  /** Дерево узлов навигации и плоская карта контента — единый источник для дерева и «портянки». */
  const { groupedNodes, flatContentMap } = useMemo(() => {
    if (!conversation?.conversationAttrValues) {
      return { groupedNodes: [] as NavigationNode[], flatContentMap: new Map() };
    }
    const { nodes, flatContentMap: map } = buildGroupedNavigationData(
      conversation.conversationAttrValues,
      groupsForHierarchy,
      attributeDefinitions,
      t('dialogs:noGroup'),
    );
    return { groupedNodes: nodes, flatContentMap: map };
  }, [conversation?.conversationAttrValues, groupsForHierarchy, attributeDefinitions, t]);

  /**
   * Итоговое дерево с переводом стандартных названий синтетических узлов
   * («Общее» внутри групп) и опциональным верхнеуровневым узлом «Общее»
   * для `MainFieldsPanel`.
   */
  const displayNodes = useMemo(() => {
    const translateGeneralNodes = (nodes: NavigationNode[]): NavigationNode[] =>
      nodes.map((node) => {
        const endsWithGeneral = node.id.endsWith(`/${ROOT_GENERAL_NODE_ID}`);
        return {
          ...node,
          name: endsWithGeneral ? t('dialogs:general') : node.name,
          children: node.children ? translateGeneralNodes(node.children) : undefined,
        };
      });

    const translated = translateGeneralNodes(groupedNodes);
    if (showMainFields && conversation) {
      return [{ id: MAIN_FIELDS_NODE_ID, name: t('dialogs:general') }, ...translated];
    }
    return translated;
  }, [groupedNodes, t, showMainFields, conversation]);

  // Пользовательский порядок верхнеуровневых блоков — общий для меню и «портянки».
  const { orderedNodes, reorderTopLevelNodes } = useTopLevelNodesOrder(displayNodes);

  const flatNodes = useMemo<FlatNode[]>(() => flattenNodes(orderedNodes), [orderedNodes]);

  const { collapsedNodeIds, toggleSection, expandSection, visibleNodeIds } = useCollapsibleSections(orderedNodes);

  // Свёрнутость меню навигации и его ширина — настройки пользователя, а не состояние просмотра карточки.
  const { isNavigationOpen, setNavigationOpen } = useNavigationCollapse();
  const { navigationWidth, setNavigationWidth } = useNavigationWidth();

  // Ширина меню на время перетаскивания границы приходит из `--attr-nav-width-temp`,
  // которую ставит `NavigationResizeHandle`; сохранённая ширина — запасное значение.
  const navigationSidebarStyle = useMemo(
    () =>
      ({
        ['--attr-nav-width']: `${navigationWidth}px`,
        ['--sidebar-width']: 'var(--attr-nav-width-temp, var(--attr-nav-width))',
        ['--sidebar-width-icon']: COLLAPSED_NAVIGATION_WIDTH,
      }) as CSSProperties,
    [navigationWidth],
  );

  // Список секционных id в document-order для scrollspy. Секции внутри свёрнутых групп
  // в «портянке» не отрисованы — их не отслеживаем, иначе подсветка залипнет на скрытом.
  const sectionIds = useMemo(() => visibleNodeIds.map(toSectionId), [visibleNodeIds]);

  const activeSectionId = useScrollSpy(sectionIds, scrollContainerRef);
  const activeNodeId = fromSectionId(activeSectionId);

  // Прокрутка к якорю при изменении URL hash (входная навигация + клики по дереву).
  useHashScroll();

  // Запрос на прокрутку к секции. Объект (а не строка) — чтобы повторный выбор
  // того же узла тоже приводил к прокрутке.
  const [scrollRequest, setScrollRequest] = useState<{ sectionId: string } | null>(null);

  const handleNavSelect = useCallback(
    (nodeId: string) => {
      // Секция может быть внутри свёрнутой группы — разворачиваем путь к ней,
      // иначе переход по меню закончится на пустом заголовке.
      expandSection(nodeId);
      const sectionId = toSectionId(nodeId);
      // Пишем hash через router replace — без распухания истории.
      navigate(`${pathname}${search}#${encodeURIComponent(sectionId)}`, { replace: true });
      // Прокрутку запрашиваем отдельно: секции разворачиваются в следующем рендере,
      // а `useHashScroll` не срабатывает при повторном выборе того же узла.
      setScrollRequest({ sectionId });
    },
    [expandSection, navigate, pathname, search],
  );

  useEffect(() => {
    const container = scrollContainerRef.current;
    if (!scrollRequest || !container) return;

    const scrollToSection = () => {
      const el = container.querySelector<HTMLElement>(`#${CSS.escape(scrollRequest.sectionId)}`);
      scrollIntoViewWithinContainer(el, { behavior: 'smooth' });
    };
    scrollToSection();

    // Развернувшаяся секция анимирует высоту: пока анимация идёт, прокручивать
    // ещё некуда — повторяем прокрутку, когда высота «портянки» стала итоговой.
    const handleAnimationEnd = () => {
      container.removeEventListener('animationend', handleAnimationEnd);
      scrollToSection();
    };
    container.addEventListener('animationend', handleAnimationEnd);
    return () => container.removeEventListener('animationend', handleAnimationEnd);
  }, [scrollRequest]);

  const nodeDropdownOptions = useMemo(
    () =>
      flatNodes.map((node) => ({
        type: 'item' as const,
        key: node.id,
        variant: node.id === activeNodeId ? ('active' as const) : ('default' as const),
        title: <span style={{ paddingLeft: node.depth * 12 }}>{applyNbspTypography(node.name)}</span>,
        onClick: () => handleNavSelect(node.id),
      })),
    [flatNodes, activeNodeId, handleNavSelect],
  );

  const activeNodeName = useMemo(() => {
    if (flatNodes.length === 0) return undefined;
    const name = flatNodes.find((n) => n.id === activeNodeId)?.name;
    return name ? applyNbspTypography(name) : undefined;
  }, [flatNodes, activeNodeId]);

  if (orderedNodes.length === 0) return null;

  const scroller = (
    <ScrollArea
      className={cn('flex-1 min-h-0', SCROLL_AREA_VIEWPORT_BLOCK)}
      viewPortProps={{ ref: scrollContainerRef }}
    >
      <ContentScroller
        nodes={orderedNodes}
        flatContentMap={flatContentMap}
        attributeDefinitions={attributeDefinitions}
        conversation={conversation}
        toSectionId={toSectionId}
        collapsedNodeIds={collapsedNodeIds}
        onToggleSection={toggleSection}
      />
    </ScrollArea>
  );

  if (isMobile) {
    return (
      <div className="flex flex-col overflow-hidden h-full min-h-0 gap-3">
        <Dropdown options={nodeDropdownOptions}>
          <Button variant="outline" size="sm" className="mr-auto min-w-0 max-w-full">
            <span className="truncate">{activeNodeName}</span>
            <ChevronDownIcon className="size-4 shrink-0" />
          </Button>
        </Dropdown>
        {scroller}
      </div>
    );
  }

  return (
    // Собственный контекст сайдбара: панель навигации по атрибутам сворачивается
    // независимо от сайдбара приложения и панели транскрипта, которые лежат выше по дереву.
    // Состояние — из настроек пользователя: карточка открывается таким, каким он оставил меню.
    <SidebarProvider
      ref={sidebarContextRef}
      open={isNavigationOpen}
      onOpenChange={setNavigationOpen}
      allowShortcut={false}
      className="flex overflow-hidden h-full min-h-0 w-full"
      style={navigationSidebarStyle}
    >
      <NavigationSidebar
        nodes={orderedNodes}
        onNodeSelect={handleNavSelect}
        selectedNodeId={activeNodeId}
        onReorder={reorderTopLevelNodes}
      />
      {/* Ширину меняет только развёрнутое меню: у свёрнутого ширина — место под переключатель.
          Граница живёт внутри таба атрибутов и делит меню и значения, поэтому ширину панели
          транскрипта, заданную выше по дереву, она не трогает */}
      {isNavigationOpen && (
        <NavigationResizeHandle
          sidebarContextRef={sidebarContextRef}
          width={navigationWidth}
          onWidthChange={setNavigationWidth}
        />
      )}
      {scroller}
    </SidebarProvider>
  );
};
